import { isPartnerGI } from "@tm/insurance/utils";
import {
  PdtContext,
  PdtEventPayload,
  PdtEventTrackingContext,
  PdtLogParams,
  PdtPageContext,
} from "./types";

const PDT_TEMPLATE_ID = 193673;
const PDT_TOPIC_NAME = "mmt_insurance_b2c_pdt_client_metric_logging";
const PDT_ENV = "prod";
const PDT_SESSION_VISIT_NUMBER = 1;

const generateEventId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const normalizeEventId = (eventId: unknown): string => {
  if (typeof eventId === "string") return eventId;
  if (
    typeof eventId === "number" ||
    typeof eventId === "boolean" ||
    typeof eventId === "bigint"
  )
    return String(eventId);
  if (eventId == null) return "";
  try {
    return JSON.stringify(eventId);
  } catch {
    return String(eventId);
  }
};

const getTimezone = (): string => {
  try {
    if (
      typeof Intl !== "undefined" &&
      typeof Intl.DateTimeFormat === "function"
    ) {
      const resolved = Intl.DateTimeFormat().resolvedOptions();
      if (resolved?.timeZone) return resolved.timeZone;
    }
  } catch {}
  return "NA";
};

const SESSION_ID_KEY = "pdt_session_id";

const getSessionId = (): string => {
  if (typeof window === "undefined" || !window.sessionStorage) return "";
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
};

const getBaseEventTrackingContext = (): PdtEventTrackingContext => ({
  template_id: PDT_TEMPLATE_ID,
  topic_name: PDT_TOPIC_NAME,
  env: PDT_ENV,
  timezone: getTimezone(),
  funnel_source: isPartnerGI() ? "gi" : "mmt",
  session_id: getSessionId(),
  session_visit_number: PDT_SESSION_VISIT_NUMBER,
});

const getBaseContext = (): PdtContext => ({
  template_id: PDT_TEMPLATE_ID,
  topic_name: PDT_TOPIC_NAME,
});

const getBasePageContext = (page_name: string): PdtPageContext => ({
  page_name,
  // Required by PDT schema (as observed via SDK validation errors)
  page_type: "landing",
  funnel_step: "landing",
  lob: "travelinsurance",
  lob_category: "travelinsurance",
  page_url: typeof window !== "undefined" ? window.location.href : "",
});

const normalizeUserInput = (params: PdtLogParams) => {
  const event_id = normalizeEventId(params.event_id);
  const event_name =
    typeof params.event_name === "string" ? params.event_name : "";
  const event_value = params.event_value ?? "";
  const page_name =
    typeof params.page_name === "string" && params.page_name.length > 0
      ? params.page_name
      : typeof window !== "undefined"
      ? window.location.href
      : "";
  const event_tracking_context = params.event_tracking_context;
  const page_context = params.page_context;
  return {
    event_id,
    event_name,
    event_value,
    page_name,
    event_tracking_context,
    page_context,
  };
};

const formatForSdk = (
  userData: ReturnType<typeof normalizeUserInput>
): PdtEventPayload => {
  const event_id =
    typeof userData.event_id === "string" && userData.event_id.length > 0
      ? userData.event_id
      : generateEventId();
  const event_timestamp = Date.now();
  const event_tracking_context: PdtEventTrackingContext = {
    ...getBaseEventTrackingContext(),
    ...userData.event_tracking_context,
  };
  return {
    page_context: {
      ...getBasePageContext(userData.page_name),
      ...userData.page_context,
    },
    event_tracking_context,
    context: getBaseContext(),
    event_detail: {
      event_id,
      event_timestamp,
      device_details_extended: {},
      user_details_extended: {},
      event_name: userData.event_name,
      event_type: "action",
      event_value: userData.event_value,
    },
  };
};

export const getMergedPdtEvent = (params: PdtLogParams): PdtEventPayload => {
  const userData = normalizeUserInput(params);
  return formatForSdk(userData);
};
